import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { projectKeys } from '../queries/keys';
import { projectPaths } from '../queries/paths';

// TSS vite/03 — server state for the `projects` entity.

export const PROJECTS_PAGE_SIZE = 25;   // the backend default; hard cap is 100

// GET /api/v1/admin/projects -> { items, pagination }
//
// The FIRST list in this API that takes real filters: client_id,
// project_state and bootstrap_phase, on top of limit/offset. Clients, users
// and teams take none, which is why only this section has a filter bar.
//
// Empty filters are dropped instead of travelling as '': the backend turns a
// query parameter into an equality, and `project_state=''` would ask for the
// projects whose state is the empty string — none of them.
export function useProjectsList({
    limit = PROJECTS_PAGE_SIZE, offset = 0, clientId, projectState, q,
} = {}) {
    const params = { limit, offset };
    if (clientId) params.client_id = clientId;
    if (projectState) params.project_state = projectState;
    // Searches name and slug. Under two characters the backend ignores it and
    // answers the plain list, so there is no "type more" state to build here.
    if (q) params.q = q;

    return useQuery({
        queryKey: projectKeys.list(params),
        queryFn: ({ signal }) => api
            .get(projectPaths.list(), { params, signal })
            .then(r => r.data),
        placeholderData: keepPreviousData,
    });
}

// GET /admin/projects/{id} answers the WHOLE project in one response: the
// client, the repositories, the tiers, the assigned teams with each scope
// explained in words, and the current directives. The edit form only reads
// the base fields off it; the rest is there for the screens that come next.
export function useProject(id) {
    return useQuery({
        queryKey: projectKeys.detail(id),
        queryFn: ({ signal }) => api.get(projectPaths.detail(id), { signal }).then(r => r.data),
        enabled: !!id,
    });
}

// GET /admin/projects/{id}/teams — who works on this project, with each team's
// members and roles. It can answer SEVERAL teams and that is not an error:
// they come ordered by which one has the most active work here.
export function useProjectTeams(id) {
    return useQuery({
        queryKey: projectKeys.teams(id),
        queryFn: ({ signal }) => api.get(projectPaths.teams(id), { signal }).then(r => r.data),
        enabled: !!id,
    });
}

export function useCreateProject() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(projectPaths.list(), payload).then(r => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.all }),
    });
}

export function useUpdateProject(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.patch(projectPaths.detail(id), payload).then(r => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: projectKeys.detail(id) });
            qc.invalidateQueries({ queryKey: projectKeys.all });
        },
    });
}

// The picker's fetcher. It lives here because the endpoint does: a form asking
// for options must not know the URL.
//
// It takes the search term and NOT a page: a picker's job is to find one
// record, and past twenty results the answer is to type more, not to paginate.
export function searchProjects(q, signal) {
    const params = { limit: 20, offset: 0 };
    if (q) params.q = q;
    return api
        .get(projectPaths.list(), { params, signal })
        .then(r => r.data.items ?? []);
}

// Resolves the project a saved filter or field points at, when the screen does
// not already know its name. Without it a filter set to project 57 reads back
// as `#57`, which looks like missing data rather than like a name that was
// never fetched.
export function resolveProject(id, signal) {
    return api
        .get(projectPaths.detail(id), { signal })
        .then(r => r.data?.project ?? r.data)
        .catch(() => null);
}
